import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import Text from '@/components/Text';

type Position = 'top';

interface TriangleProps {
  $position: Position;
}

const Triangle = styled.div<TriangleProps>`
  width: 26px;
  height: 14px;
  position: absolute;
  overflow: hidden;

  ${({ $position }) =>
    $position === 'top' &&
    css`
      left: 0;
      right: 0;
      bottom: -14px;
      margin: auto;
      transform: rotate(180deg);
    `}
  &::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    background: ${({ theme }) => theme.colors.white};
    transform: rotate(45deg);
    top: 8px;
    left: 5px;
    box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

interface BoxProps {
  $left: number;
  $top: number;
  $position: Position;
  $width: number;
}

const Box = styled.div<BoxProps>`
  position: fixed;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 11;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};

  ${({ $position, $left, $top }) =>
    $position === 'top' &&
    css`
      left: ${$left}px;
      top: ${$top}px;
    `}
`;

type Props = PropsWithChildren<{
  title: string;
  position?: Position;
  className?: string;
  width?: number;
}>;

const Tooltiped: FC<Props> = ({
  title,
  position = 'top',
  children,
  className,
  width = 100,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const [ref, setRef] = useState<HTMLDivElement | null>(null);

  const tooltipRef = useRef<HTMLDivElement>(null);

  const domRect = tooltipRef.current?.getBoundingClientRect();
  const tpWidth = ref?.getBoundingClientRect()?.width ?? 0;

  useEffect(() => {
    const callback = () => setShowTooltip(false);
    window.addEventListener('scroll', callback, true);

    return () => {
      window.removeEventListener('scroll', callback, true);
    };
  }, []);

  return (
    <div
      onMouseEnter={(e) => {
        e.stopPropagation();
        setShowTooltip(true);
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        setShowTooltip(false);
      }}
      ref={tooltipRef}
    >
      {children}
      {createPortal(
        <>
          {!!tooltipRef.current && showTooltip && (
            <Box
              ref={(ref) => setRef(ref)}
              $left={domRect!.x - (tpWidth - domRect!.width) / 2}
              $top={domRect!.y - domRect!.height + window.scrollY - 3}
              $position={position}
              $width={width}
              className={className}
            >
              <Triangle $position={position} />
              <Text $type="smallHeadline">{title}</Text>
            </Box>
          )}
        </>,
        document.getElementById('modals-root')!,
      )}
    </div>
  );
};

export default Tooltiped;
